import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { FileSignature, TrendingUp, Loader2, ChevronRight, PoundSterling } from 'lucide-react';
import { AreaChart, Area, Tooltip, ResponsiveContainer } from 'recharts';

const PERIODS = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'week', label: 'This week' },
  { key: 'month', label: 'This month' },
  { key: 'today', label: 'Today' },
  { key: 'custom', label: 'Custom' },
];

const gbp = (v) =>
  `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

/**
 * Headline dashboard stat. Filterable by day / week / month / custom range.
 * Shows how many contracts the CUSTOMER signed in the period, and the total
 * estimated profit (labour cost + 30% of hardware cost, taken from each
 * contract's Quotation Breakdown).
 */
export const PeriodProfitWidget = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('last7');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trend, setTrend] = useState(null);

  useEffect(() => {
    api.get('/dashboard/profit-trend', { params: { weeks: 8 } })
      .then((r) => setTrend(r.data?.points || []))
      .catch(() => setTrend([]));
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') {
        if (!start) { setLoading(false); return; }
        params.start = start;
        if (end) params.end = end;
      }
      const { data } = await api.get('/dashboard/contract-metrics', { params });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [period, start, end]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <Card className="border-slate-200 overflow-hidden" data-testid="period-profit-widget">
      <CardContent className="p-0">
        {/* Period selector */}
        <div className="flex flex-wrap items-center gap-2 px-5 pt-5">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPeriod(p.key)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${
                period === p.key
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
              }`}
              data-testid={`period-btn-${p.key}`}
            >
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex items-center gap-2 ml-1">
              <Input
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                className="h-8 w-36 text-xs"
                data-testid="period-custom-start"
              />
              <span className="text-slate-400 text-xs">to</span>
              <Input
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                className="h-8 w-36 text-xs"
                data-testid="period-custom-end"
              />
            </div>
          )}
        </div>

        {/* Stats */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-100 mt-5">
            <div className="bg-white p-6 flex items-center gap-4" data-testid="stat-signed-count">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                <FileSignature className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Contracts signed</p>
                <p className="text-3xl font-bold text-slate-900 mt-0.5">{data?.signed_count ?? 0}</p>
                <p className="text-xs text-slate-400">{data?.period_label || ''}</p>
              </div>
            </div>
            <div className="bg-white p-6 flex items-center gap-4" data-testid="stat-total-value">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                <PoundSterling className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Contracts value</p>
                <p className="text-3xl font-bold text-slate-900 mt-0.5">{gbp(data?.total_value)}</p>
                <p className="text-xs text-slate-400">Total system value (ex-VAT)</p>
              </div>
            </div>
            <div className="bg-white p-6 flex items-center gap-4" data-testid="stat-total-profit">
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Estimated profit</p>
                <p className="text-3xl font-bold text-emerald-700 mt-0.5">{gbp(data?.total_profit)}</p>
                <p className="text-xs text-slate-400">Labour + 30% of hardware</p>
              </div>
            </div>
          </div>
        )}

        {/* Weekly profit sparkline — last 8 weeks (independent of the period filter) */}
        {trend && trend.length > 0 && (
          <div className="border-t border-slate-100 px-5 py-3" data-testid="profit-trend-sparkline">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-slate-500">Weekly profit — last 8 weeks</p>
              <p className="text-xs text-slate-400">
                {trend[trend.length - 1]?.week_label} · {gbp(trend[trend.length - 1]?.profit)}
              </p>
            </div>
            <ResponsiveContainer width="100%" height={48}>
              <AreaChart data={trend} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                <defs>
                  <linearGradient id="profitSpark" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip
                  cursor={{ stroke: '#cbd5e1', strokeWidth: 1 }}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 8px' }}
                  labelFormatter={(_, p) => (p && p[0] ? `Week of ${p[0].payload.week_label}` : '')}
                  formatter={(v, _n, p) => [`${gbp(v)} · ${p?.payload?.count || 0} signed`, 'Profit']}
                />
                <Area
                  type="monotone"
                  dataKey="profit"
                  stroke="#059669"
                  strokeWidth={2}
                  fill="url(#profitSpark)"
                  dot={{ r: 2, fill: '#059669' }}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Signed jobs breakdown */}
        {!loading && (data?.jobs?.length > 0) && (
          <div className="border-t border-slate-100 divide-y divide-slate-50" data-testid="signed-jobs-list">
            {data.jobs.slice(0, 6).map((j) => (
              <button
                key={j.pack_id}
                type="button"
                onClick={() => navigate(`/customers/${j.customer_id}`)}
                className="w-full flex items-center justify-between gap-3 px-5 py-2.5 hover:bg-slate-50 text-left"
                data-testid={`signed-job-${j.customer_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{j.customer_name}</p>
                  <p className="text-xs text-slate-400 truncate">
                    {j.address}{j.postcode ? `, ${j.postcode}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span className="text-xs text-slate-500 tabular-nums">{gbp(j.value)}</span>
                  <span className="text-sm font-semibold text-emerald-700 tabular-nums">{gbp(j.profit)}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300" />
                </div>
              </button>
            ))}
            {data.jobs.length > 6 && (
              <p className="px-5 py-2 text-xs text-slate-400">+{data.jobs.length - 6} more</p>
            )}
          </div>
        )}
        {!loading && (data?.signed_count === 0) && (
          <div className="border-t border-slate-100 px-5 py-4 text-sm text-slate-400" data-testid="no-signed-jobs">
            No contracts signed in this period.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PeriodProfitWidget;
